import { useMemo, useState } from "react";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { cn } from "@/shared/libs/utils";
import { useKeywordFilter } from "@/shared/hooks";
import { useCollectionNewErrorColumns } from "../hooks/useCollectionErrorsColumns";
import type { CollectionErrorRowAPI } from "../types/collection-types";
import type { SortingState } from "@tanstack/react-table";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: CollectionErrorRowAPI[];
}

export function CollectionErrorsDrawer({ open, onOpenChange, rows }: Props) {
  const columns = useCollectionNewErrorColumns();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);

  const filter = useKeywordFilter(rows, (r) => [
    r.trans_id,
    r.channel || "",
    r.sub_channel || "",
    r.dw_account_id,
    r.err_msg || "",
    r.org_name || "",
    r.workspace_ids.join(", "),
  ]);

  // 정렬 적용
  const sortedRows = useMemo(() => {
    if (sorting.length === 0) return filter.filteredRows;

    const sorted = [...filter.filteredRows];
    const sort = sorting[0];
    
    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof CollectionErrorRowAPI];
      const bValue = b[sort.id as keyof CollectionErrorRowAPI];

      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sort.desc ? -comparison : comparison;
    });

    return sorted;
  }, [filter.filteredRows, sorting]);

  // 페이지네이션 적용
  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, page, pageSize]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // ✅ right drawer layout
          "fixed right-0 top-0 left-auto h-screen w-full max-w-none translate-x-0 translate-y-0 rounded-none p-6 sm:w-[70vw]",

          // ✅ override center-modal animations + make it slide from right
          "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:slide-in-from-right-[100%] data-[state=closed]:slide-out-to-right-[100%]",

          // ✅ neutralize vertical slide that DialogContent base class applies
          "data-[state=open]:slide-in-from-top-[0%] data-[state=closed]:slide-out-to-top-[0%]"
        )}
      >
        <div className="min-w-0 h-full overflow-y-auto">
          <DataTable
            columns={columns}
            data={paginatedRows}
            totalCount={sortedRows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            isLoading={false}
            isError={false}
            toolbar={
              <DataTableToolbar
                searchValue={filter.keyword}
                onSearchChange={(v) => {
                  filter.setKeyword(v);
                  setPage(1);
                }}
                showReset={!!filter.keyword}
                onReset={() => {
                  filter.reset();
                  setPage(1);
                }}
                searchPlaceholder="오류 검색 (trans_id, channel, err_msg...)"
              />
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
